'use client';

import React, { useCallback, useMemo, useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import LoaderSmall from '/public/icons/loader-small.svg';

pdfjs.GlobalWorkerOptions.workerSrc = '/pdf-worker/pdf.worker.4.8.69.min.js';

export interface PdfPreviewProps {
  fileUrl: string;
  containerWidth: number;
  containerHeight: number;
  onLoadSuccess?: () => void;
  onLoadError?: (error: Error) => void;
  className?: string;
}

const PdfPreviewClient: React.FC<PdfPreviewProps> = ({
  fileUrl,
  containerWidth,
  containerHeight,
  onLoadSuccess,
  onLoadError,
  className,
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isReady, setIsReady] = useState(false);

  const handleDocumentLoadSuccess = useCallback(() => {
    setIsLoading(false);
    setIsReady(true);
    onLoadSuccess?.();
  }, [onLoadSuccess]);

  const handleDocumentLoadError = useCallback(
    (error: Error) => {
      console.error('[PdfPreview] Error loading PDF:', error);
      setIsLoading(false);
      onLoadError?.(error);
    },
    [onLoadError]
  );

  const pdfOptions = useMemo(
    () => ({
      cMapUrl: '/cmaps/',
      cMapPacked: true,
      standardFontDataUrl: '/standard_fonts/',
    }),
    []
  );

  return (
    <div
      className={`relative w-full h-full flex items-center justify-center rounded-lg overflow-hidden ${className}`}
      style={{ width: containerWidth, height: containerHeight }}
    >
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-grey-500 bg-opacity-10 z-10">
          <LoaderSmall className="w-8 h-8 text-grey-700 animate-spin" />
        </div>
      )}
      <Document
        file={fileUrl}
        onLoadSuccess={handleDocumentLoadSuccess}
        onLoadError={handleDocumentLoadError}
        options={pdfOptions}
        loading={null} // We use our own isLoading state and loader
        className="flex items-center justify-center w-full h-full"
      >
        {isReady && (
          <Page
            pageNumber={1}
            width={containerWidth}
            height={containerHeight}
            renderAnnotationLayer={false}
            renderTextLayer={false}
            className="[&_canvas]:max-w-full [&_canvas]:max-h-full [&_canvas]:object-contain"
          />
        )}
      </Document>
    </div>
  );
};

export default PdfPreviewClient;